import React, { useEffect, useMemo, useState } from 'react';
import { Modal, withStyles } from '@material-ui/core';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import {
  deleteLoadItem,
  setLoadValues,
  restoreOriginalLoad,
  updateLoadList,
  newLoadItem,
  searchLoadList,
} from '~/store/dailyparts/actions';
import { useTranslation } from 'react-i18next';
import { getOperationTypes } from '~/store/manager/actions';
import PF2MDataTable from '~/components/PF2MDataTable';
import {
  formatDate,
  formatTime,
  newMomentDate,
  parseDateToTimestamp,
  subtractDaysFromNow,
  parseDate,
  formatDateToServerFormat,
} from '~/utils/moment';
import usePlatformConfiguration, {
  usePlatformConfigurationUserAccessLevel,
} from '~/services/platformConfiguration';
import EditHistoryModal from '~/components/EditHistoryModal';
import styles from '../styles';
import EditHistoryButton from '~/components/EditHistoryButton';
import { usePagination } from '../hooks';

// const EXCAVATOR_ID = 1;
const TRUCK_ID = 2;
const WHEEL_LOADER = 15;

const getTruckByIdTruck = (trucks, idTruck) => trucks.find(t => Number(t.id) === Number(idTruck));
const createTruckGroupFieldInLoadList = (equipments, loadList) => {
  const trucks = equipments;
  return loadList.map((l) => {
    const truck = getTruckByIdTruck(trucks, l.id_truck);
    return {
      ...l,
      id_group_truck: truck?.id_group ?? '',
      control_type_id: l?.control_type_id ?? 1,
    };
  });
};

const LoadList = () => {
  const { t: translate } = useTranslation();

  const filters = useSelector(state => state.dailyParts.filters);
  const userAccessLevel = useSelector(state => state.auth.user.level);
  const equipmentType = useSelector(
    state => state.manager.equipmentTypes.find(r => r.id === filters.equipmentType), shallowEqual,
  );
  const turns = useSelector(state => state.manager.turns);
  const teams = useSelector(state => state.manager.teams);
  const elements = useSelector(state => state.manager.elements);
  const subElements = useSelector(state => state.manager.subElements);
  const excavatorGroups = useSelector(state => state.manager.equipmentsGroups
    .filter(e => e.id_equipament === equipmentType.id), shallowEqual);
  const equipments = useSelector(state => state.manager.equipments);
  const truckGroups = useSelector(state => state.manager.equipmentsGroups
    .filter(e => e.id_equipament === TRUCK_ID), shallowEqual);
  const trucks = useSelector(state => state.manager.equipments
    .filter(e => e.id_equip === TRUCK_ID), shallowEqual);
  const loadListState = useSelector(state => state.dailyParts.loadList);
  const loadList = useMemo(() => createTruckGroupFieldInLoadList(equipments, loadListState),
    [loadListState, equipments]);
  const ogLoadList = useSelector(state => state.dailyParts.originalLoadList);
  const loadPagination = useSelector(
    state => state.dailyParts.pagination.loadPagination,
  );


  const operatorsGroups = useSelector(
    state => state.manager.operatorsGroups.filter(o => o.name), shallowEqual,
  );
  const operators = useSelector(state => state.manager.operators);
  const materials = useSelector(state => state.manager.materials);
  const exceptionTypes = useSelector(state => state.manager.exceptionTypes);
  const operationTypes = useSelector(state => state.manager.operationTypes
    .filter(e => e.id_equipment_type === equipmentType.id), shallowEqual);

  const controlTypes = useSelector(state => state.manager.controlTypes);

  const closures = useSelector(state => state.manager.closures);
  const { value: userLimitEditConfig } = usePlatformConfigurationUserAccessLevel(userAccessLevel);
  const { value: enableClosure } = usePlatformConfiguration('enable_closure');

  const [openEditHistoryModal, setOpenEditHistoryModal] = useState(false);
  const [selectedEditHistoryData, setSelectedEditHistoryData] = useState(null);

  const dispatch = useDispatch();

  const validateDates = key => (row) => {
    const currentDatetime = newMomentDate();

    const stDate = row.start_date || formatDate(currentDatetime);
    const stTime = row.start_time || formatTime(currentDatetime);
    const endDate = row.end_date || formatDate(currentDatetime);
    const endTime = row.end_time || formatTime(currentDatetime);

    const startDateTimestamp = parseDateToTimestamp(stDate, stTime);
    const endDateTimestamp = parseDateToTimestamp(endDate, endTime);

    const isEnableClosureOn = enableClosure && closures?.length > 0;
    let userLimitEdit = subtractDaysFromNow(userLimitEditConfig);

    if (isEnableClosureOn) {
      const dateNow = newMomentDate();
      const lastClosureDate = parseDate(closures[0].timestampClose, true);
      const currentDaysFromLastClosure = dateNow.diff(lastClosureDate, 'days');

      const lockEditConfig = subtractDaysFromNow(userLimitEditConfig);
      const lockEditLastClosure = subtractDaysFromNow(currentDaysFromLastClosure);

      userLimitEdit = currentDaysFromLastClosure > userLimitEditConfig
        ? lockEditConfig : lockEditLastClosure;
    }

    if (key === 'end_date' && startDateTimestamp > endDateTimestamp) {
      return { error: true, helperText: translate('validation:FinalDateSmallerThanInitialDate') };
    }

    const invalidEditPeriodDateMsg = {
      isValid: false, helperText: translate('validation:InvalidPeriodDate'),
    };

    if (key === 'start_date' && parseDate(row[key]).isBefore(userLimitEdit)) return invalidEditPeriodDateMsg;

    return { isValid: true };
  };

  const columns = [
    {
      title: 'ID',
      editable: 'never',
      field: 'id',
    },
    {
      editable: 'never',
      title: translate('common:EditHistory'),
      field: 'edit_history',
      render: row => (
        <EditHistoryButton
          exceptionType={row.exception_type}
          onClick={() => {
            setOpenEditHistoryModal(true);
            setSelectedEditHistoryData(row);
          }}
        />
      ),
    },
    {
      editable: 'always',
      title: translate('common:InitialDate'),
      field: 'start_date',
      customType: 'date',
      // validate: validateDates('start_date'),
    },
    {
      editable: 'always',
      title: translate('common:FinalDate'),
      field: 'end_date',
      customType: 'date',
      validate: validateDates('end_date'),
    },
    {
      editable: 'always',
      title: translate('common:InitialHour'),
      field: 'start_time',
      customType: 'time',
      validate: validateDates('start_time'),
    },
    {
      editable: 'always',
      title: translate('common:FinalHour'),
      field: 'end_time',
      customType: 'time',
      validate: validateDates('end_time'),
    },
    {
      editable: 'never',
      title: translate('common:Duration'),
      field: 'duration',
      customType: 'numeric',
    },
    {
      editable: 'always',
      title: translate('common:Shift'),
      field: 'turn',
      lookup: turns.map(e => ({
        id: e.id,
        name: `${e.turn_group_name} - ${e.name}`,
        active: e.active,
      })),
      validate: row => row.turn !== '0'
        && row.turn !== 0 && Boolean(row.turn),
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Team'),
      field: 'team_id',
      lookup: teams,
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:OperatorGroup'),
      field: 'id_group_excavator_operator',
      lookup: operatorsGroups,
      customOnChange: () => ({ id_excavator_operator: '0' }),
      validate: row => row.id_group_excavator_operator !== '0',
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Operator'),
      field: 'id_excavator_operator',
      customType: 'filterLookup',
      lookupKey: 'id_operator',
      lookup: operators,
      lookupFilter: (e, f) => String(e.id_group) === String(f.id_group_excavator_operator)
        || String(f.id_group_excavator_operator) === '0',
      validate: row => row.id_operator !== '0',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Material'),
      field: 'id_material_esc',
      customType: 'filterLookup',
      lookup: materials,
      validate: row => row.id_material_esc && row.id_material_esc !== '0',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:EquipmentGroup'),
      field: 'id_excavator_group',
      lookup: excavatorGroups,
      validate: row => row.id_excavator_group && row.id_excavator_group !== '0',
      customType: 'filterLookup',
      tooltip: true,
      customOnChange: () => ({ id_excavator: '0' }),
    },
    {
      editable: 'always',
      title: translate('common:Equipment'),
      field: 'id_excavator',
      lookup: equipments.filter(e => e.id_equip === equipmentType.id),
      validate: row => row.id_excavator && row.id_excavator !== '0',
      customType: 'filterLookup',
      lookupFilter: (option, row) => String(option.id_group) === String(row.id_excavator_group) || String(row.id_excavator_group) === '0',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:OperationType'),
      field: 'id_operation_type',
      lookup: operationTypes,
      validate: row => row.id_operation_type && row.id_operation_type !== '0',
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:TruckEquipmentGroup'),
      field: 'id_group_truck',
      lookup: truckGroups,
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:TruckEquipment'),
      field: 'id_truck',
      lookup: [{ id: '0', name: translate('common:None') }, ...trucks],
      customType: 'filterLookup',
      lookupFilter: (option, row) => {
        const idTruck = Number(option.id);
        const truck = trucks.find(t => t.id === idTruck);
        const idGroupTruck = Number(truck?.id_group);
        const rowIdGroupTruck = Number(row.id_group_truck);
        return idGroupTruck === rowIdGroupTruck || option.id === '0';
      },
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Origin'),
      field: 'id_mine',
      customType: 'filterLookup',
      lookup: elements,
      validate: row => row.id_mine && row.id_mine !== '0',
      customOnChange: () => ({ id_mine_origin: '0' }),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:OriginPoint'),
      field: 'id_mine_origin',
      customType: 'filterLookup',
      lookupFilter: (option, row) => (
        String(option.id_element) === String(row.id_mine)
        || String(row.id_mine) === '0'
      ),
      lookup: subElements,
      validate: row => row.id_mine_origin && row.id_mine_origin !== '0',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Destination'),
      field: 'id_destination_esc',
      customType: 'filterLookup',
      lookup: elements,
      validate: row => row.id_destination_esc && row.id_destination_esc !== '0',
      customOnChange: () => ({ id_destination_point_esc: '0' }),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:DestinationPoint'),
      customType: 'filterLookup',
      field: 'id_destination_point_esc',
      lookupFilter: (option, row) => (
        String(option.id_element) === String(row.id_destination_esc)
        || String(row.id_destination_esc) === '0'
      ),
      lookup: subElements,
      validate: row => row.id_destination_point_esc && row.id_destination_point_esc !== '0',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:MaterialWeight'),
      field: 'load_manager',
      validate: row => !!row.load_manager || row.load_manager === 0,
      type: 'numeric',
    },
    {
      editable: 'always',
      title: translate('common:LoadTime'),
      field: 'load_time',
      validate: row => !Number.isNaN(Number.parseInt(row.load_time)),
      type: 'numeric',
    },
    {
      editable: 'always',
      title: translate('common:IdleTime'),
      field: 'stop_time',
      validate: row => !Number.isNaN(Number.parseInt(row.stop_time)),
      type: 'numeric',
    },
    {
      editable: 'always',
      title: translate('common:CodeTime'),
      field: 'code_time',
      validate: row => !Number.isNaN(Number.parseInt(row.code_time)),
      type: 'numeric',
    },
    {
      editable: 'never',
      title: translate('common:EntryType'),
      field: 'exception_type',
      lookup: exceptionTypes,
      validate: row => row.exception_type && row.exception_type !== '0',
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Latitude'),
      field: 'load_lat',
      type: 'numeric',
    },
    {
      editable: 'always',
      title: translate('common:Longitude'),
      field: 'load_lon',
      type: 'numeric',
    },
    {
      editable: 'always',
      title: translate('common:Altitude'),
      field: 'load_alt',
      type: 'numeric',
    },
    {
      editable: 'always',
      title: translate('common:ControlType'),
      field: 'control_type_id',
      customType: 'filterLookup',
      lookup: [{ id: '0', name: translate('common:None') }, ...controlTypes],
      lookupFilter: option => (
        option.equip_type_id === equipmentType.id || !option.equip_type_id
      ),
      tooltip: true,
    },
  ];

  const customEditHistoryColumns = [
    {
      title: translate('common:Operator'),
      field: 'id_excavator_operator',
      customType: 'filterLookup',
      lookupKey: 'id_operator',
      lookup: operators,
    },
    {
      title: translate('common:TruckEquipmentGroup'),
      field: 'id_group_truck',
      customType: 'filterLookup',
      lookup: truckGroups,
    },
  ];

  useEffect(() => {
    if (equipmentType.id === WHEEL_LOADER) {
      dispatch(getOperationTypes());
    }
  }, [equipmentType, dispatch]);

  const filterToFormFieldMap = {
    turn: 'turn',
    equipment: 'id_excavator',
    equipmentGroup: 'id_excavator_group',
    operatorGroup: 'id_group_excavator_operator',
    operator: 'id_excavator_operator',
    material: 'id_material_esc',
    startOrigin: 'id_mine',
    endOrigin: 'id_destination_esc',
    startSubOrigin: 'id_mine_origin',
    endSubOrigin: 'id_destination_point_esc',
  };

  const filterToFormMultiFieldMap = {
    equipmentGroup: 'id_excavator_group',
    equipment: 'id_excavator',
  };

  const mappedFilterData = Object.entries(filterToFormFieldMap).reduce(
    (acc, [filterKey, formField]) => {
      if (filters[filterKey] !== undefined && filters[filterKey] !== 0) {
        acc[formField] = filters[filterKey];
      }
      return acc;
    },
    {},
  );

  const mappedFilterMultiData = Object.entries(filterToFormMultiFieldMap).reduce(
    (acc, [filterKey, formField]) => {
      const filterValues = filters[filterKey];

      // adiciona para initialFormData somente
      // se tiver apenas um item selecionado no multiselect
      if (filterValues && filterValues.length === 1) {
        const [firstValue] = filterValues;
        acc[formField] = firstValue;
      } else {
      // se tiver mais de um ou nenhum, seta como '0'
        acc[formField] = '0';
      }
      return acc;
    },
    {},
  );

  const { applyDefaultSort, ...paginationOptions } = usePagination({
    filters,
    dispatch,
    listType: 'load',
    pagination: loadPagination,
    searchAction: searchLoadList,
  });

  const { page = 0, total = 0, loadingMore = false } = loadPagination || { };

  const initialFormData = {
    ...columns.reduce((acc, e) => ({ ...acc, [e.field]: '0' }), {}),
    start_date: formatDate(newMomentDate(), 'DD-MM-YYYY'),
    end_date: formatDate(newMomentDate(), 'DD-MM-YYYY'),
    start_time: formatTime(newMomentDate(), 'HH:mm:ss'),
    end_time: formatTime(newMomentDate(), 'HH:mm:ss'),
    ...mappedFilterData,
    ...mappedFilterMultiData,
  };

  const formatToDatetime = (date, time) => `${formatDateToServerFormat(date)}${formatTime(time)}`;

  const getOriginalLoadData = (originalLoadList, currentLoadList) => (
    originalLoadList
      .filter(originalRow => currentLoadList.some(({ id }) => id === originalRow.id))
      .map((originalRow) => {
        const truck = getTruckByIdTruck(trucks, originalRow.id_truck);

        return {
          ...originalRow,
          start_datetime: formatToDatetime(originalRow.start_date, originalRow.start_time),
          end_datetime: formatToDatetime(originalRow.end_date, originalRow.end_time),
          id_group_truck: truck?.id_group ?? '',
        };
      })
  );

  return (
    <>
      <PF2MDataTable
        title={translate('common:Cycles')}
        page={page}
        totalCount={total}
        isLoading={loadingMore}
        initialFormData={initialFormData}
        columns={applyDefaultSort(columns)}
        data={loadList}
        options={{
          pageSize: 10,
          pageSizeOptions: [10, 20, 50, 100],
          showTitle: true,
          search: true,
          selection: true,
          cacheKey: 'daily_parts_loads',
          maxBodyHeight: 'calc(100vh - 290px)',
          minBodyHeight: 'calc(100vh - 290px)',
          showFirstLastPageButtons: false,
          ...paginationOptions,
        }}
        onAdd={newData => dispatch(newLoadItem(newData))}
        onChange={(newData) => {
          newData.forEach((row) => {
            dispatch(setLoadValues(row));
          });
        }}
        onDiscard={() => dispatch(restoreOriginalLoad())}
        onApply={() => {
          const actionData = {
            data: loadList,
            parameters: filters,
            saveLog: true,
            ogData: getOriginalLoadData(ogLoadList, loadList),
          };

          dispatch(updateLoadList(actionData));
        }}
        onDelete={(newData) => {
          newData.forEach((e) => {
            dispatch(deleteLoadItem(e));
          });
        }}
      />
      <Modal
        open={openEditHistoryModal}
        onClose={() => setOpenEditHistoryModal(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <EditHistoryModal
          listName="load"
          referenceColumns={columns}
          selectedEditHistoryData={selectedEditHistoryData}
          handleClose={() => setOpenEditHistoryModal(false)}
          customEditHistoryColumns={customEditHistoryColumns}
        />
      </Modal>
    </>
  );
};

LoadList.propTypes = {};

LoadList.defaultProps = {};

export default withStyles(styles)(LoadList);
